/**
 * 상태 배지의 «톤» — 모양(`statusKind`) 위에 얹는 옅은 바탕 · 같은 계열 글자.
 *
 * eric 2026-09-30: 「준비 → 레시피에서 상태 부분에.. 칩들 색 좀 넣어줘. 너무 단순해.」
 *
 * 🔴 **톤은 모양을 대체하지 않는다.** 점(● ◐ ○) · 선 스타일 · 글자는 그대로고 톤은 그 위에 얹힌다
 *    (「색만으로 상태를 구분하지 않는다」).
 * 🔴 **톤은 «호출부가 고른 도메인»에만 선다.** `StatusBadge` 는 원값만 받아 도메인을 모른다 — `archived` ·
 *    `draft` 같은 낱말이 다른 도메인에서 와도 톤이 저절로 붙지 않게, 도메인별 표를 여기 한 곳에 두고
 *    호출부가 `tone` 으로 넘긴다. 지금은 **레시피 상태 셋뿐**이다. 작업 · 알람 · 시료 · 모듈 배지는 톤이 없다.
 * ⚠ 다른 칩으로 넓힐 때는 여기에 그 도메인의 표를 더한다. 같은 뜻(확정 · 작성 중 · 보관)은 같은 톤을 쓴다.
 * ⚠ 색은 토큰이 정한다(`theme.css` 의 `--tone-*`). `:root`(흑백 앱 넷)에서는 종전 픽셀 그대로다.
 *
 * 톤 셋의 뜻:
 *   accent = 강조(파랑) — «확정된 것»
 *   warn   = 주의(호박) — «아직 · 대기». 🔴 **주의 계단을 읽는 자리는 주의 배지 · 주의 행 줄무늬 · 일시정지 공정 칸뿐이다**
 *            (팀장 결정 2026-09-30 · 시안 채택 2026-10-01) — 배너 · 버튼 · 알람 본문에 쓰지 마라. 위험(붉은색)이 아니다: 막힌 것이 아니라 «끝나지 않은 것»이다
 *   muted  = 가장 옅은 중성 — «물러난 것»
 *   ok     = 성공 초록 — «지금 잘 돌고 있는 것»(디자인팀 시안 `.pill--ok` · eric 2026-10-12 · 작업 줄 「실행 중」 · 사용자 「활성」)
 *   danger = 위험 붉은색 — 알람 심각도 오류 · 긴급뿐(eric 2026-10-12)
 */

import { statusKind } from './statusKind.ts'

export type StatusTone = 'accent' | 'warn' | 'muted' | 'ok' | 'danger'

/**
 * 레시피 상태 → 톤.
 *   확정   = 강조 — 옅은 강조 면 · 강조 본색 글자 · 강조 본색 테
 *   작성 중 = 주의 — 옅은 호박 면 · 짙은 호박 글자 · 호박 점선 테(점선 · 반채움 점은 종류가 긋는다)
 *   보관   = 가장 옅은 중성 면 · 흐린 글자
 */
export const RECIPE_STATUS_TONE = {
  draft: 'warn',
  committed: 'accent',
  archived: 'muted',
} as const satisfies Record<string, StatusTone>

export function recipeStatusTone(status: string): StatusTone | undefined {
  return (RECIPE_STATUS_TONE as Record<string, StatusTone | undefined>)[status]
}

/**
 * 레시피 상태 칩을 테 없는 알약(`flat`)으로 그릴지 — **바탕이 있는 칩만** 테를 걷는다(eric 2026-10-30 「여기 확정도.. 테두리 없애줘」).
 *   바탕은 톤(목록 · 상세 머리 — 확정 강조 · 작성 중 호박 · 보관 중성)이나 바탕을 까는 종류(완료 `done` · 실행 `run` · 위험 `err`)가 깐다.
 *   톤 없이 그리는 작성 중(`wait` 점선) · 보관(`idle`)은 바탕이 없어 테를 걷으면 글자만 남는다 — 테를 둔다(`severityBadgeFlat` 과 같은 원칙).
 */
export function recipeBadgeFlat(status: string, tone: StatusTone | undefined): boolean {
  if (tone !== undefined) return true
  const kind = statusKind(status)
  return kind === 'done' || kind === 'run' || kind === 'err'
}

/*
 * ⚠ 작업(배치) 상태 톤(`BATCH_STATUS_TONE` · 실행 중 = 초록 `ok`)은 걷었다(eric 2026-10-14 「실행 중.. 로고색으로.. 완료.. 테마색으로.. 전체적으로 통일」).
 *   실행 중 · 완료는 상태 종류(`.a-stb--run` · `.a-stb--done`)가 곧바로 로고색 채움 · 테마색 옅은 면을 입는다(`--status-*` · theme.css).
 */

/**
 * 사용자 상태 → 톤(eric 2026-10-12 「사용자에서 상태.. 디자인팀의 색상으로」). 시안의 알약 뜻을 그대로 옮겼다 —
 *   활성 = `.pill--ok`(초록) · 잠김 = `.pill--warn`(호박 · ▲ — 시간이 지나면 풀리는 «아직») · 비활성 = `.pill--idle`(중성)
 */
export const USER_STATUS_TONE = {
  active: 'ok',
  locked: 'warn',
  disabled: 'muted',
} as const satisfies Record<string, StatusTone>

export function userStatusTone(status: string): StatusTone | undefined {
  return (USER_STATUS_TONE as Record<string, StatusTone | undefined>)[status]
}

/**
 * 알람 심각도 → 톤(eric 2026-10-12 「심각도에.. 경고에 색상을 넣어줘」 — 그 대신 행 왼쪽 띠는 걷었다).
 *   긴급 · 오류 = 위험(붉은색) · 경고 = 주의(호박 · ▲) · 정보 = 톤 없음
 */
export const ALARM_SEVERITY_TONE = {
  CRITICAL: 'danger',
  ERROR: 'danger',
  WARN: 'warn',
} as const satisfies Record<string, StatusTone>

export function alarmSeverityTone(severity: string): StatusTone | undefined {
  return (ALARM_SEVERITY_TONE as Record<string, StatusTone | undefined>)[severity]
}

/**
 * 심각도 배지를 테 없는 알약(`flat`)으로 그릴지 — **바탕이 있는 칩만** 테를 걷는다(eric 2026-10-30).
 *   바탕은 톤(경고 호박 · 오류 · 긴급 붉은색)이나 종류 `err`(오류 · 긴급 채움)가 깐다. 정보(INFO · 톤 없음 · `idle`)와
 *   톤 없이 그리는 경고(로그 · 알람 상세 · `wait` 점선)는 바탕이 없어 테를 걷으면 글자만 남는다 — 테를 둔다
 *   (eric 「이거.. 칩 테두리를 만들어줘」 · 로그 심각도 열의 「정보」).
 * ⚠ `StatusBadge` 원자에서 가르지 않는 이유: 시료 상태 칩은 바탕 없는 값도 일부러 테를 걷었다(eric 2026-10-29).
 */
export function severityBadgeFlat(severity: string, tone: StatusTone | undefined): boolean {
  return tone !== undefined || statusKind(severity) === 'err'
}
